import React, { useState, useEffect } from 'react';
import { boardCompositionAPI, userAPI } from '../api';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Users, Plus, Edit, Trash2, Loader2, Save, Calendar, UserCheck, Crown, History
} from 'lucide-react';
import { toast } from 'sonner';

const POSITIONS = [
  'Chairman of the Board',
  'Vice Chairman',
  'President / CEO',
  'Director',
  'Independent Director',
  'Corporate Secretary',
  'Treasurer',
  'Compliance Officer',
];

const MEMBER_TYPES = [
  { value: 'executive', label: 'Executive' },
  { value: 'non_executive', label: 'Non-Executive' },
  { value: 'independent', label: 'Independent' },
  { value: 'regular', label: 'Regular' },
];

const BoardComposition = () => {
  const [compositions, setCompositions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const [form, setForm] = useState({
    effective_date: new Date().toISOString().split('T')[0],
    notes: '',
    members: [],
  });

  const [memberForm, setMemberForm] = useState({
    full_name: '',
    position: '',
    member_type: 'regular',
    appointed_date: '',
    term_end_date: '',
    status: 'active',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [compRes, currentRes, usersRes] = await Promise.all([
        boardCompositionAPI.getAll(),
        boardCompositionAPI.getCurrent(),
        userAPI.getAll(),
      ]);
      setCompositions(compRes.data || []);
      setCurrent(currentRes.data?.has_composition ? currentRes.data : null);
      setOrgUsers(usersRes.data || []);
    } catch (err) {
      console.error('Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  const openNewComposition = () => {
    setEditing(null);
    const members = current?.members?.map(m => ({ ...m })) || [];
    setForm({
      effective_date: new Date().toISOString().split('T')[0],
      notes: '',
      members,
    });
    setDialogOpen(true);
  };

  const openEditComposition = (comp) => {
    setEditing(comp);
    setForm({
      effective_date: comp.effective_date || '',
      notes: comp.notes || '',
      members: comp.members?.map(m => ({ ...m })) || [],
    });
    setDialogOpen(true);
  };

  const addMember = () => {
    if (!memberForm.full_name || !memberForm.position) {
      toast.error('Name and position are required');
      return;
    }
    setForm(prev => ({
      ...prev,
      members: [...prev.members, { ...memberForm }],
    }));
    setMemberForm({ full_name: '', position: '', member_type: 'regular', appointed_date: '', term_end_date: '', status: 'active', notes: '' });
  };

  const removeMember = (idx) => {
    setForm(prev => ({
      ...prev,
      members: prev.members.filter((_, i) => i !== idx),
    }));
  };

  const handleSave = async () => {
    if (form.members.length === 0) {
      toast.error('Add at least one member');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await boardCompositionAPI.update(editing.id, form);
        toast.success('Board composition updated');
      } else {
        await boardCompositionAPI.create(form);
        toast.success('Board composition saved');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await boardCompositionAPI.delete(id);
      toast.success('Composition deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-[#00A3A3]" />
      </div>
    );
  }

  const statusColor = {
    active: 'bg-green-100 text-green-700',
    resigned: 'bg-yellow-100 text-yellow-700',
    removed: 'bg-red-100 text-red-700',
    term_ended: 'bg-gray-100 text-gray-600',
  };

  return (
    <div className="space-y-6" data-testid="board-composition">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#1A1D21] flex items-center gap-2">
            <Crown className="h-5 w-5 text-[#F58A26]" />
            Board Composition
          </h2>
          <p className="text-sm text-[#5A6570]">Track and manage board member composition over time</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
            data-testid="toggle-history-btn"
          >
            <History className="h-4 w-4 mr-1" />
            {showHistory ? 'Current' : 'History'}
          </Button>
          <Button
            size="sm"
            onClick={openNewComposition}
            className="bg-[#00A3A3] hover:bg-[#008585]"
            data-testid="new-composition-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            New Snapshot
          </Button>
        </div>
      </div>

      {/* Current Board */}
      {!showHistory && (
        current ? (
          <Card className="border-[#E2E6EA]">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Current Board Composition</CardTitle>
                  <CardDescription>Effective: {current.effective_date}</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => openEditComposition(current)} data-testid="edit-current-btn">
                  <Edit className="h-4 w-4 mr-1" /> Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="current-board-table">
                  <thead>
                    <tr className="border-b border-[#E2E6EA]">
                      <th className="text-left py-2 px-3 font-medium text-[#5A6570]">Name</th>
                      <th className="text-left py-2 px-3 font-medium text-[#5A6570]">Position</th>
                      <th className="text-left py-2 px-3 font-medium text-[#5A6570]">Type</th>
                      <th className="text-left py-2 px-3 font-medium text-[#5A6570]">Status</th>
                      <th className="text-left py-2 px-3 font-medium text-[#5A6570]">Term End</th>
                    </tr>
                  </thead>
                  <tbody>
                    {current.members?.map((m, i) => (
                      <tr key={i} className="border-b border-[#F0F2F4] last:border-0">
                        <td className="py-2.5 px-3 font-medium text-[#1A1D21]">{m.full_name}</td>
                        <td className="py-2.5 px-3 text-[#5A6570]">{m.position}</td>
                        <td className="py-2.5 px-3">
                          <Badge variant="outline" className="text-xs capitalize">{m.member_type?.replace('_', ' ')}</Badge>
                        </td>
                        <td className="py-2.5 px-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor[m.status] || 'bg-gray-100 text-gray-600'}`}>
                            {m.status?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-[#5A6570]">{m.term_end_date || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {current.notes && <p className="text-sm text-[#5A6570] mt-3 italic">{current.notes}</p>}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-[#E2E6EA]">
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 mx-auto mb-3 text-[#A8B1B9]" />
              <h3 className="font-semibold text-[#1A1D21] mb-1">No Board Composition</h3>
              <p className="text-sm text-[#5A6570] mb-4">Create your first board composition snapshot</p>
              <Button size="sm" onClick={openNewComposition} className="bg-[#00A3A3] hover:bg-[#008585]">
                <Plus className="h-4 w-4 mr-1" /> Create Snapshot
              </Button>
            </CardContent>
          </Card>
        )
      )}

      {/* History */}
      {showHistory && (
        <div className="space-y-3">
          {compositions.length === 0 ? (
            <Card className="border-[#E2E6EA]">
              <CardContent className="py-8 text-center">
                <History className="h-8 w-8 mx-auto mb-2 text-[#A8B1B9]" />
                <p className="text-sm text-[#5A6570]">No composition history yet</p>
              </CardContent>
            </Card>
          ) : (
            compositions.map((comp) => (
              <Card key={comp.id} className="border-[#E2E6EA]">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium text-[#1A1D21]">Effective: {comp.effective_date}</p>
                      <p className="text-xs text-[#A8B1B9]">{comp.members?.length || 0} members</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditComposition(comp)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(comp.id)} className="text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {comp.members?.map((m, i) => (
                      <div key={i} className="flex items-center gap-1.5 bg-[#F0F2F4] px-2 py-1 rounded text-xs">
                        <UserCheck className="h-3 w-3 text-[#00A3A3]" />
                        <span className="font-medium">{m.full_name}</span>
                        <span className="text-[#A8B1B9]">- {m.position}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'New'} Board Composition</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Effective Date</Label>
                <Input
                  type="date"
                  value={form.effective_date}
                  onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                  data-testid="effective-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="e.g., Post-ASM election"
                  data-testid="composition-notes-input"
                />
              </div>
            </div>

            {/* Current members in this snapshot */}
            <div>
              <Label className="mb-2 block">Members ({form.members.length})</Label>
              {form.members.length > 0 && (
                <div className="space-y-2 mb-3 max-h-[200px] overflow-y-auto">
                  {form.members.map((m, i) => (
                    <div key={i} className="flex items-center justify-between bg-[#F0F2F4] rounded px-3 py-2">
                      <div>
                        <span className="font-medium text-sm">{m.full_name}</span>
                        <span className="text-xs text-[#5A6570] ml-2">{m.position}</span>
                        <Badge variant="outline" className="ml-2 text-[10px]">{m.member_type}</Badge>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeMember(i)} className="h-7 w-7">
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add member form */}
            <Card className="border-[#E2E6EA] bg-[#FAFBFC]">
              <CardContent className="p-4 space-y-3">
                <p className="text-sm font-medium text-[#1A1D21]">Add Member</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Full Name</Label>
                    <Input
                      value={memberForm.full_name}
                      onChange={(e) => setMemberForm({ ...memberForm, full_name: e.target.value })}
                      placeholder="Full Name"
                      className="h-9"
                      data-testid="member-name-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Position</Label>
                    <Select value={memberForm.position} onValueChange={(v) => setMemberForm({ ...memberForm, position: v })}>
                      <SelectTrigger className="h-9" data-testid="member-position-select">
                        <SelectValue placeholder="Select position" />
                      </SelectTrigger>
                      <SelectContent>
                        {POSITIONS.map(p => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Type</Label>
                    <Select value={memberForm.member_type} onValueChange={(v) => setMemberForm({ ...memberForm, member_type: v })}>
                      <SelectTrigger className="h-9" data-testid="member-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MEMBER_TYPES.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Term End Date</Label>
                    <Input
                      type="date"
                      value={memberForm.term_end_date}
                      onChange={(e) => setMemberForm({ ...memberForm, term_end_date: e.target.value })}
                      className="h-9"
                      data-testid="member-term-end-input"
                    />
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={addMember} data-testid="add-member-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Member
                </Button>
              </CardContent>
            </Card>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="save-composition-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              {editing ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BoardComposition;
